import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { EntryCard } from './TTWeekView';

function fmtEntryDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function groupByDate(entries) {
  const map = new Map();
  const sorted = [...entries].sort((a, b) => {
    const da = a.entryDate || a.createdAt || '';
    const db = b.entryDate || b.createdAt || '';
    return da < db ? -1 : da > db ? 1 : 0;
  });
  for (const e of sorted) {
    const key = e.entryDate || (e.createdAt ? e.createdAt.slice(0, 10) : 'unknown');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }
  return map;
}

function fmtGroupLabel(dateStr) {
  if (!dateStr || dateStr === 'unknown') return 'Unknown date';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function isTodayStr(dateStr) {
  const today = new Date();
  const t = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
  return dateStr === t;
}

export default function TTAgendaView({ entries, projectColor, onEdit, onDelete, onAddDay }) {
  const groups = groupByDate(entries);

  if (groups.size === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography sx={{ fontSize: 14, color: '#CBD5E1' }}>No entries in this period.</Typography>
        <Typography sx={{ fontSize: 12, color: '#E2E8F0', mt: 0.5 }}>Use Week or Day view to add entries.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 720, mx: 'auto' }}>
      {[...groups.entries()].map(([dateKey, dayEntries]) => {
        const totalHours = dayEntries.reduce((s, e) => s + (e.workingHours || 0), 0);
        const isToday = isTodayStr(dateKey);
        return (
          <Box key={dateKey}>
            {/* Date header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: isToday ? '#427898' : '#1E293B' }}>
                  {fmtGroupLabel(dateKey)}
                </Typography>
                {isToday && (
                  <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.75, bgcolor: '#DBEAFE', display: 'inline-block' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 0.5 }}>Today</Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', flexShrink: 0 }}>
                {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)}h
              </Typography>
            </Box>

            {/* Entries */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 0 }}>
              {dayEntries.map(entry => (
                <EntryCard key={entry.id} entry={entry} projectColor={projectColor} onEdit={onEdit} onDelete={onDelete} compact={false} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
